import { format } from "date-fns";
import { AnimatePresence, motion } from "framer-motion";
import {
  AlertCircle,
  Bell,
  Calendar,
  Check,
  MessageSquare,
  X,
} from "lucide-react";
import { useState } from "react";

interface Notification {
  id: string;
  type: "appointment" | "message" | "alert";
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  actionRequired?: boolean;
}

const Notifications = () => {
  const [notifications, setNotifications] = useState<Notification[]>([
    {
      id: "1",
      type: "appointment",
      title: "New Appointment Request",
      message:
        "Sarah Johnson requested a haircut appointment for tomorrow at 2 PM",
      timestamp: new Date(),
      read: false,
      actionRequired: true,
    },
    {
      id: "2",
      type: "message",
      title: "New Message",
      message: "Mike Smith: Can you confirm my appointment time for next week?",
      timestamp: new Date(Date.now() - 3600000),
      read: false,
    },
    {
      id: "3",
      type: "alert",
      title: "Inventory Alert",
      message: "Hair color products running low. Please reorder soon.",
      timestamp: new Date(Date.now() - 7200000),
      read: true,
      actionRequired: true,
    },
  ]);

  const [filter, setFilter] = useState<"all" | "unread" | "actionRequired">(
    "all"
  );

  const handleMarkAsRead = (id: string) => {
    setNotifications(
      notifications.map((notif) =>
        notif.id === id ? { ...notif, read: true } : notif
      )
    );
  };

  const handleDismiss = (id: string) => {
    setNotifications(notifications.filter((notif) => notif.id !== id));
  };

  const filteredNotifications = notifications.filter((notif) => {
    if (filter === "unread") return !notif.read;
    if (filter === "actionRequired") return notif.actionRequired;
    return true;
  });

  const getIcon = (type: string) => {
    switch (type) {
      case "appointment":
        return <Calendar className="h-5 w-5 text-blue-500" />;
      case "message":
        return <MessageSquare className="h-5 w-5 text-green-500" />;
      case "alert":
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      default:
        return <Bell className="h-5 w-5 text-gray-500" />;
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-gray-900">Notifications</h1>
        <div className="flex space-x-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as typeof filter)}
            className="rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
          >
            <option value="all">All</option>
            <option value="unread">Unread</option>
            <option value="actionRequired">Action Required</option>
          </select>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg divide-y divide-gray-200">
        <AnimatePresence>
          {filteredNotifications.map((notification) => (
            <motion.div
              key={notification.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, x: -100 }}
              className={`p-4 ${notification.read ? "bg-white" : "bg-blue-50"}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3">
                  {getIcon(notification.type)}
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">
                      {notification.title}
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      {notification.message}
                    </p>
                    <p className="mt-1 text-xs text-gray-400">
                      {format(notification.timestamp, "MMM d, yyyy h:mm a")}
                    </p>
                  </div>
                </div>
                <div className="flex space-x-2">
                  {!notification.read && (
                    <button
                      onClick={() => handleMarkAsRead(notification.id)}
                      className="p-1 rounded-full hover:bg-gray-100"
                      title="Mark as read"
                    >
                      <Check className="h-4 w-4 text-green-500" />
                    </button>
                  )}
                  <button
                    onClick={() => handleDismiss(notification.id)}
                    className="p-1 rounded-full hover:bg-gray-100"
                    title="Dismiss"
                  >
                    <X className="h-4 w-4 text-red-500" />
                  </button>
                </div>
              </div>
              {notification.actionRequired && (
                <div className="mt-2">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    Action Required
                  </span>
                </div>
              )}
            </motion.div>
          ))}
        </AnimatePresence>

        {filteredNotifications.length === 0 && (
          <div className="p-4 text-center text-gray-500">
            No notifications to display
          </div>
        )}
      </div>
    </div>
  );
};

export default Notifications;
